// Scripted fake backend for Case Chat (dev mock and tests).
//
// The fake is a FUNCTION OF THE CLOCK. A turn is `{turnId, scenarioId, startedAt,
// afterSeq, stopAt?}`; each scenario is data, a list of `{at, ev}` steps with `at`
// in milliseconds from the turn's start. `events?after=N&wait=S` computes the
// elapsed time, returns every step due since N, and when nothing is new waits
// (100 ms ticks, watching the abort signal) until the next step is due or
// min(S, 3) seconds pass. Nothing is timer-driven, so a reload mid-turn, a
// second tab and Stop all work for free, and tests drive it with an injected
// clock. Chats live in localStorage so a reload or another tab sees the same
// chats.
//
// Questions are routed by the first matching word (case-insensitive): error,
// fatal, empty, interrupt, busy, slow, table, long, draft|email; anything else is
// the default `summary`. A retry (`retry_of`) always plays `summary`.
//
// Fault knob `?mockChat=`: unavailable | list-error | send-error | send-slow |
// poll-error | poll-hang | poll-404.
//
// Console handle for walkthroughs: window.__devMockChat = { reset(), speed(n),
// scenarios, setClock(fn) }.
import type { ChatEvent, WireMessage } from '../chat/types';
import { faultMode } from './faults';
import { mockResponse, type MockRoute } from './fetchInterceptor';
import type { MockState } from './seeds';

type Step = { at: number; ev: Omit<ChatEvent, 'seq' | 't'> };
interface Scenario {
  id: string;
  match: RegExp;
  build: (turnId: string) => Step[];
  /** The script ends without a terminal event (the worker "died"). */
  interrupted?: boolean;
}

interface MockTurn {
  turnId: string;
  clientTurnId: string;
  scenarioId: string;
  startedAt: number;
  afterSeq: number;
  /** Elapsed ms at which Stop resolved the turn. */
  stopAt?: number;
  text: string;
  retryOf: string | null;
  sentAt: string;
}

interface MockChat {
  id: string;
  caseId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  turns: MockTurn[];
}

interface Store {
  chats: Record<string, MockChat>;
}

const STORAGE_KEY = 'devMock:chat:v1';
const SEQ_STRIDE = 3; // seq has gaps on purpose: the client must treat it as an opaque cursor
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MAX_CHARS = 50_000;
const ELL = '…';

// ---------------------------------------------------------------- scripts

const DEFAULT_ANSWER = `## Royalty shortfall: what the documents show

The documents point to a **June 2023 shortfall of about $18,000** in royalty distributions.

- The Q2 2023 royalty schedule flags the shortfall month (DEMO-0000010, spreadsheet at DEMO-0000011).
- Beacon's counsel served a notice of default on 2023-05-03 (DEMO-0000024).
- The §4.2 minimum royalty was in the draft circulated on 2022-02-15 (DEMO-0000001).

| Document | Date | What it shows |
|---|---|---|
| DEMO-0000001 | 2022-02-15 | Revised licensing draft v3 sent to Beacon counsel |
| DEMO-0000010 | 2023-07-10 | Bookkeeper flags a shortfall month |
| DEMO-0000024 | 2023-05-03 | Notice of default |

One timing point to check: the notice (2023-05-03) predates the schedule that documents the shortfall (2023-07-10), so confirm which payments the notice refers to.`;

const TABLE_ANSWER = `## Payment terms across the license drafts

Payment windows range from 5~10 days (or 20~30 days) depending on the draft, and the party names are placeholders such as <Company Name> and <Licensee>.

| Document | Date | Amount | Window | Source | Notes |
|:---|---:|---:|:---:|---|---|
| DEMO-0000001 | 2022-02-15 | $18,000 | 5~10 days | Draft v3 | Minimum royalty<br>applies from year two |
| DEMO-0000010 | 2023-07-10 | $4,250.75 | 30 days | Schedule Q2 | Flags the shortfall month |
| DEMO-0000011 | 2023-07-10 | $4,250.75 | 30 days | Spreadsheet | Same figures as the schedule |
| DEMO-0000024 | 2023-05-03 | n/a | 10 days | Notice | Cure period stated in section 3 |
| 2:19-cv-01234 | 11/03/2021 | n/a | n/a | Docket | Complaint filed |
| DEMO-0000031 | 2021-11-03T09:30:00Z | $0 | n/a | Email | Cover note only |

~~Superseded~~ terms are struck through. A code sample:

\`\`\`
royalty = max(minimum_royalty, net_sales * rate)   # section 4.2, applied quarterly, with no cap on the number of quarters considered
\`\`\`

An image is shown as its text only: ![tracking pixel](https://example.invalid/pixel.png)

A [safe link](https://example.com/terms) and an [unsafe one](javascript:alert(1)).

An unbroken identifier: ${'ROYALTYSCHEDULE'.repeat(6)}`;

const DRAFT_ANSWER = `Dear Counsel,

We write regarding the royalty distributions for the second quarter of 2023. The schedule produced at DEMO-0000010 and the accompanying spreadsheet at DEMO-0000011 show a shortfall of approximately $18,000 for June 2023.

Your client's notice of default dated May 3, 2023 (DEMO-0000024) predates that schedule, so please confirm which payments the notice was meant to address.

We would welcome a call this week to resolve the point without further correspondence.

Sincerely,
Acme Legal`;

function longAnswer(): string {
  const parts: string[] = ['## Timeline of the licensing relationship\n'];
  for (let i = 1; i <= 27; i += 1) {
    parts.push(
      `### Phase ${i}\n\nIn phase ${i} the parties exchanged ${i + 2} drafts. The Q${(i % 4) + 1} schedule (DEMO-00000${String(10 + i).padStart(2, '0')}) records the minimum royalty, and the cover email of 2022-${String((i % 12) + 1).padStart(2, '0')}-15 explains the change. - A detail worth checking: the figures in this phase do not match the earlier draft, so confirm them against the signed agreement before relying on them.\n`,
    );
  }
  return parts.join('\n');
}

function streamText(text: string, from: number, to: number): Step[] {
  const slots = Math.max(1, Math.floor((to - from) / 100));
  const size = Math.max(1, Math.ceil(text.length / slots));
  const steps: Step[] = [];
  for (let i = 0, n = 0; i < text.length; i += size, n += 1) {
    steps.push({ at: from + n * 100, ev: { type: 'text', text: text.slice(i, i + size) } });
  }
  return steps;
}

const status = (at: number, text: string): Step => ({ at, ev: { type: 'status', text } });

function summaryFlow(turnId: string, answer: string = DEFAULT_ANSWER): Step[] {
  return [
    status(0, `Getting started${ELL}`),
    status(700, `Getting oriented in the case${ELL}`),
    { at: 1700, ev: { type: 'text', text: 'Let me look at the documents first.' } },
    { at: 1800, ev: { type: 'retract' } },
    status(1800, `Searching documents${ELL}`),
    status(3200, `Reading documents${ELL}`),
    status(5200, `Checking tagged issues${ELL}`),
    status(6400, `Thinking${ELL}`),
    status(7600, `Drafting${ELL}`),
    ...streamText(answer, 7700, 16_000),
    { at: 16_100, ev: { type: 'done', message_id: `m-${turnId}`, text: answer } },
  ];
}

function shortAnswerFlow(turnId: string, answer: string, phrases: [number, string][], streamFrom: number, streamTo: number): Step[] {
  return [
    ...phrases.map(([at, text]) => status(at, text)),
    ...streamText(answer, streamFrom, streamTo),
    { at: streamTo + 100, ev: { type: 'done', message_id: `m-${turnId}`, text: answer } },
  ];
}

export const SCENARIOS: Scenario[] = [
  {
    id: 'error',
    match: /error/i,
    build: () => [
      status(0, `Getting started${ELL}`),
      status(1500, `Searching documents${ELL}`),
      {
        at: 3000,
        ev: {
          type: 'error',
          code: 'model_busy',
          text: 'The AI service is busy right now. Please try again in a moment.',
          retryable: true,
        },
      },
    ],
  },
  {
    id: 'fatal',
    match: /fatal/i,
    build: () => [
      status(0, `Getting started${ELL}`),
      {
        at: 2000,
        ev: {
          type: 'error',
          code: 'context_full',
          text: 'This conversation has grown too long to continue. Please start a new chat.',
          retryable: false,
        },
      },
    ],
  },
  {
    id: 'empty',
    match: /empty/i,
    build: (turnId) => [
      status(0, `Getting started${ELL}`),
      status(1500, `Drafting${ELL}`),
      { at: 2500, ev: { type: 'done', message_id: `m-${turnId}`, text: '   ' } },
    ],
  },
  {
    id: 'interrupt',
    match: /interrupt/i,
    interrupted: true,
    build: () => [
      status(0, `Getting started${ELL}`),
      status(1500, `Searching documents${ELL}`),
      status(3000, `Thinking${ELL}`),
    ],
  },
  {
    id: 'busy',
    match: /busy/i,
    build: (turnId) => [
      status(0, `Getting started${ELL}`),
      status(2000, `The AI service is busy, retrying${ELL}`),
      status(8000, `The AI service is busy, retrying${ELL}`),
      status(9000, `Thinking${ELL}`),
      status(9500, `Drafting${ELL}`),
      ...streamText(DEFAULT_ANSWER, 9600, 17_000),
      { at: 17_100, ev: { type: 'done', message_id: `m-${turnId}`, text: DEFAULT_ANSWER } },
    ],
  },
  {
    id: 'slow',
    match: /slow/i,
    build: (turnId) => [
      status(0, `Getting started${ELL}`),
      status(1500, `Searching documents${ELL}`),
      status(30_000, `Working${ELL}`),
      status(44_000, `Drafting${ELL}`),
      ...streamText(DEFAULT_ANSWER, 44_100, 45_000),
      { at: 45_100, ev: { type: 'done', message_id: `m-${turnId}`, text: DEFAULT_ANSWER } },
    ],
  },
  {
    id: 'table',
    match: /table/i,
    build: (turnId) =>
      shortAnswerFlow(
        turnId,
        TABLE_ANSWER,
        [
          [0, `Getting started${ELL}`],
          [600, `Searching documents${ELL}`],
          [1600, `Drafting${ELL}`],
        ],
        1700,
        6_000,
      ),
  },
  {
    id: 'long',
    match: /long/i,
    build: (turnId) => {
      const text = longAnswer();
      const to = 7_600 + Math.ceil(text.length / 24) * 100;
      return shortAnswerFlow(
        turnId,
        text,
        [
          [0, `Getting started${ELL}`],
          [800, `Reading documents${ELL}`],
          [2400, `Thinking${ELL}`],
          [3000, `Drafting${ELL}`],
        ],
        3100,
        to,
      );
    },
  },
  {
    id: 'draft',
    match: /draft|e-?mail/i,
    build: (turnId) =>
      shortAnswerFlow(
        turnId,
        DRAFT_ANSWER,
        [
          [0, `Getting started${ELL}`],
          [600, `Drafting${ELL}`],
        ],
        700,
        4_500,
      ),
  },
  {
    id: 'summary',
    match: /^/,
    build: (turnId) => summaryFlow(turnId),
  },
];

export const scenarioFor = (question: string): Scenario =>
  SCENARIOS.find((s) => s.match.test(question)) as Scenario;
const scenarioById = (id: string): Scenario => SCENARIOS.find((s) => s.id === id) ?? SCENARIOS[SCENARIOS.length - 1];

// ---------------------------------------------------------------- the clock

let clockFn: () => number = () => Date.now();
let speedFactor = 1;
const elapsedSince = (startedAt: number): number => Math.max(0, (clockFn() - startedAt) * speedFactor);

// ---------------------------------------------------------------- storage

let memoryStore: Store = { chats: {} };

function readStore(): Store {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as Store;
      if (parsed && typeof parsed === 'object' && parsed.chats) return parsed;
    }
  } catch {
    // fall through to memory
  }
  return memoryStore;
}

function writeStore(store: Store): void {
  memoryStore = store;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // memory copy is still valid
  }
}

// ---------------------------------------------------------------- playing a turn

type TurnStatus = 'running' | 'done' | 'error' | 'stopped' | 'interrupted';
interface Played {
  events: ChatEvent[];
  status: TurnStatus;
  /** Seq of the last event this turn ever produces (known once it has finished). */
  endSeq: number;
  terminal: ChatEvent | null;
}

/** The turn as it stands at the current clock. */
function playTurn(turn: MockTurn): Played {
  const scenario = scenarioById(turn.scenarioId);
  const steps = scenario.build(turn.turnId);
  const elapsed = elapsedSince(turn.startedAt);
  const t0 = turn.startedAt / 1000;
  const toEvent = (step: Step, i: number): ChatEvent => ({
    seq: turn.afterSeq + SEQ_STRIDE * (i + 1),
    t: t0 + step.at / 1000,
    ...step.ev,
  });

  if (turn.stopAt !== undefined) {
    const kept = steps.filter((s) => s.at <= (turn.stopAt as number));
    const events = kept.map(toEvent);
    // Partial text: the text events since the last retract.
    let partial = '';
    for (const s of kept) {
      if (s.ev.type === 'text') partial += s.ev.text ?? '';
      else if (s.ev.type === 'retract') partial = '';
    }
    // If the script had already ended before Stop landed, Stop changes nothing.
    const natural = kept.find((s) => s.ev.type === 'done' || s.ev.type === 'error');
    if (natural) {
      const terminal = events[kept.indexOf(natural)];
      return { events: events.slice(0, kept.indexOf(natural) + 1), status: terminal.type === 'done' ? 'done' : 'error', endSeq: terminal.seq, terminal };
    }
    const stopped: ChatEvent = {
      seq: turn.afterSeq + SEQ_STRIDE * (events.length + 1),
      t: t0 + (turn.stopAt as number) / 1000,
      type: 'stopped',
      message_id: partial.trim() ? `m-${turn.turnId}` : null,
      text: partial,
    };
    return { events: [...events, stopped], status: 'stopped', endSeq: stopped.seq, terminal: stopped };
  }

  const due = steps.filter((s) => s.at <= elapsed);
  const events = due.map(toEvent);
  const last = steps[steps.length - 1];
  const finished = elapsed >= last.at;
  const endSeq = turn.afterSeq + SEQ_STRIDE * steps.length;
  if (!finished) return { events, status: 'running', endSeq, terminal: null };
  const lastEvent = events[events.length - 1];
  if (lastEvent.type === 'done') return { events, status: 'done', endSeq, terminal: lastEvent };
  if (lastEvent.type === 'error') return { events, status: 'error', endSeq, terminal: lastEvent };
  return { events, status: 'interrupted', endSeq, terminal: null };
}

/** Merge consecutive `text` events, the way the server does when answering. */
function coalesce(events: ChatEvent[]): ChatEvent[] {
  const out: ChatEvent[] = [];
  for (const ev of events) {
    const prev = out[out.length - 1];
    if (ev.type === 'text' && prev && prev.type === 'text') {
      out[out.length - 1] = { ...prev, seq: ev.seq, t: ev.t, text: (prev.text ?? '') + (ev.text ?? '') };
    } else {
      out.push({ ...ev });
    }
  }
  return out;
}

interface ChatView {
  events: ChatEvent[];
  running: boolean;
  activeTurn: MockTurn | null;
  lastSeq: number;
}

function viewOf(chat: MockChat): ChatView {
  const events: ChatEvent[] = [];
  let running = false;
  let activeTurn: MockTurn | null = null;
  for (const turn of chat.turns) {
    const played = playTurn(turn);
    events.push(...played.events);
    if (played.status === 'running') {
      running = true;
      activeTurn = turn;
    }
  }
  return { events, running, activeTurn, lastSeq: events.length ? events[events.length - 1].seq : 0 };
}

function transcript(chat: MockChat): WireMessage[] {
  const out: WireMessage[] = [];
  chat.turns.forEach((turn, index) => {
    if (!turn.retryOf) {
      out.push({ id: `u-${turn.turnId}`, role: 'user', text: turn.text, created_at: turn.sentAt });
    }
    const played = playTurn(turn);
    const at = new Date(played.terminal?.t ? played.terminal.t * 1000 : turn.startedAt).toISOString();
    const isLatest = index === chat.turns.length - 1;
    const superseded = chat.turns.some((t) => t.retryOf === turn.turnId);
    if (played.status === 'done' && played.terminal && (played.terminal.text ?? '').trim()) {
      out.push({ id: played.terminal.message_id ?? `m-${turn.turnId}`, role: 'assistant', text: played.terminal.text ?? '', created_at: at });
    } else if (played.status === 'stopped' && played.terminal) {
      if ((played.terminal.text ?? '').trim()) {
        out.push({ id: played.terminal.message_id ?? `m-${turn.turnId}`, role: 'assistant', text: played.terminal.text ?? '', created_at: at });
      } else {
        out.push({ id: turn.turnId, role: 'note', text: 'Stopped.', turn_id: turn.turnId, created_at: at });
      }
    } else if (!superseded && (played.status === 'error' || played.status === 'interrupted' || (played.status === 'done' && !(played.terminal?.text ?? '').trim()))) {
      const interrupted = played.status === 'interrupted';
      const empty = played.status === 'done';
      // A blank `done` is not retryable (the server answers 409 retry_invalid); an interrupted worker is.
      const retryable = interrupted || played.terminal?.retryable === true;
      out.push({
        id: turn.turnId,
        role: 'error',
        text: interrupted
          ? 'That answer was interrupted.'
          : empty
            ? "We didn't get an answer back. Please try again."
            : (played.terminal?.text ?? 'Something went wrong on our side. Please try again.'),
        turn_id: turn.turnId,
        retryable: retryable && isLatest && !viewOf(chat).running,
        created_at: at,
      });
    }
  });
  return out;
}

// ---------------------------------------------------------------- helpers

const now = () => new Date().toISOString();
const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    if (signal?.aborted) onAbort();
    else signal?.addEventListener('abort', onAbort, { once: true });
  });

const err = (status: number, error: string, code: string, extra: Record<string, unknown> = {}): Response =>
  new Response(JSON.stringify({ error, code, ...extra }), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });

const codePoints = (s: string) => Array.from(s).length;

// ---------------------------------------------------------------- fault state

let sendErrorServed = false;
let pollErrorSince: number | null = null;

function resetFaultState(): void {
  sendErrorServed = false;
  pollErrorSince = null;
}

// ---------------------------------------------------------------- routes

export function buildChatRoutes(state: MockState): MockRoute[] {
  const caseOk = (caseId: string): boolean => caseId === state.caseId;
  const fault = (): string | null => faultMode('mockChat');
  const notEnabled = (): Response => err(403, "Workbench isn't available for you on this case.", 'not_enabled');
  const noCase = (): Response => err(404, 'case not found or access denied', 'chat_not_found');
  const noChat = (): Response => err(404, 'That chat could not be found.', 'chat_not_found');

  /** Common gate for every chat route except availability. */
  const gate = (caseId: string): Response | null => {
    if (fault() === 'unavailable') return notEnabled();
    if (!caseOk(caseId)) return noCase();
    return null;
  };

  const findChat = (store: Store, caseId: string, chatId: string): MockChat | null => {
    const c = store.chats[chatId];
    return c && c.caseId === caseId && !c.deleted ? c : null;
  };

  const activeTurnsOf = (store: Store): number =>
    Object.values(store.chats).filter((c) => !c.deleted && viewOf(c).running).length;

  const base = '^/cases/(?<caseId>[^/]+)';
  const chatPath = `${base}/chats/(?<chatId>[^/]+)`;

  return [
    {
      method: 'GET',
      pattern: new RegExp(`${base}/chat/availability$`),
      handler: (ctx) => {
        if (!caseOk(ctx.params.caseId) || fault() === 'unavailable') {
          return mockResponse.json({ available: false, reason: 'not_enabled' });
        }
        return mockResponse.json({ available: true });
      },
    },
    {
      // GET /cases/{id}/bates-lookup?bates=... for clickable cites: exact bates_begin first, then
      // lexicographic range containment (bates_begin <= b <= bates_end), as the real route does.
      method: 'GET',
      pattern: new RegExp(`${base}/bates-lookup$`),
      handler: (ctx) => {
        if (!caseOk(ctx.params.caseId)) return noCase();
        const bates = (ctx.url.searchParams.get('bates') ?? '').trim();
        if (!bates) return mockResponse.error(400, 'bates query param required');
        const docs = Object.values(state.docsById);
        const doc =
          docs.find((d) => d.bates_begin === bates) ??
          docs
            .filter((d) => d.bates_end && d.bates_begin <= bates && bates <= d.bates_end)
            .sort((a, b) => a.bates_begin.localeCompare(b.bates_begin))[0];
        if (!doc) return new Response(JSON.stringify({ error: 'Bates stamp not found', bates }), { status: 404, headers: { 'Content-Type': 'application/json' } });
        return mockResponse.json({
          match: 'document',
          document_id: doc.id,
          bates_begin: doc.bates_begin,
          bates_end: doc.bates_end,
          original_filename: doc.original_filename,
          page_count: null,
          doc_type: doc.doc_type,
        });
      },
    },
    {
      method: 'POST',
      pattern: new RegExp(`${base}/chat/clientlog$`),
      handler: () => new Response(null, { status: 204 }),
    },
    {
      method: 'GET',
      pattern: new RegExp(`${base}/chats$`),
      handler: (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        if (fault() === 'list-error') return mockResponse.error(500, '[devMock] injected failure for chat list');
        const store = readStore();
        const chats = Object.values(store.chats)
          .filter((c) => c.caseId === ctx.params.caseId && !c.deleted)
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
          .slice(0, 200)
          .map((c) => ({
            chat_id: c.id,
            title: c.title,
            created_at: c.createdAt,
            updated_at: c.updatedAt,
            running: viewOf(c).running,
          }));
        return mockResponse.json({ chats });
      },
    },
    {
      method: 'GET',
      pattern: new RegExp(`${chatPath}$`),
      handler: (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        const chat = findChat(readStore(), ctx.params.caseId, ctx.params.chatId);
        if (!chat) return noChat();
        const view = viewOf(chat);
        return mockResponse.json({
          chat_id: chat.id,
          title: chat.title,
          created_at: chat.createdAt,
          updated_at: chat.updatedAt,
          messages: transcript(chat),
          active_turn: view.activeTurn
            ? { turn_id: view.activeTurn.turnId, after_seq: view.activeTurn.afterSeq }
            : null,
          last_seq: view.lastSeq,
        });
      },
    },
    {
      method: 'DELETE',
      pattern: new RegExp(`${chatPath}$`),
      handler: (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        const store = readStore();
        const chat = store.chats[ctx.params.chatId];
        if (!chat || chat.caseId !== ctx.params.caseId) return noChat();
        chat.deleted = true;
        writeStore(store);
        return new Response(null, { status: 204 });
      },
    },
    {
      method: 'POST',
      pattern: new RegExp(`${chatPath}/messages$`),
      handler: async (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        const mode = fault();
        if (mode === 'send-slow') await sleep(3000, ctx.signal);
        if (mode === 'send-error' && !sendErrorServed) {
          sendErrorServed = true;
          return mockResponse.error(500, '[devMock] injected failure for chat send');
        }
        const { chatId } = ctx.params;
        const body = (ctx.body ?? {}) as { client_turn_id?: unknown; text?: unknown; retry_of?: unknown };
        const clientTurnId = typeof body.client_turn_id === 'string' ? body.client_turn_id : '';
        if (!UUID.test(chatId) || !UUID.test(clientTurnId)) {
          return err(400, 'That request was not valid.', 'bad_request');
        }
        const retryOf = typeof body.retry_of === 'string' && body.retry_of ? body.retry_of : null;
        const text = typeof body.text === 'string' ? body.text.trim() : '';
        if (!retryOf && text === '') return err(400, 'Please enter a message.', 'message_empty');
        if (!retryOf && codePoints(text) > MAX_CHARS) {
          return err(413, 'That message is too long to send.', 'message_too_long');
        }

        const store = readStore();
        const existing = store.chats[chatId];
        if (existing && (existing.caseId !== ctx.params.caseId || existing.deleted)) return noChat();
        const chat: MockChat =
          existing ??
          ({
            id: chatId,
            caseId: ctx.params.caseId,
            title: Array.from(text.replace(/\s+/g, ' ')).slice(0, 40).join('').trimEnd(),
            createdAt: now(),
            updatedAt: now(),
            deleted: false,
            turns: [],
          } as MockChat);

        // Idempotent replay of the same client_turn_id.
        const replay = chat.turns.find((t) => t.clientTurnId === clientTurnId);
        if (replay) {
          if (!retryOf && replay.text !== text) {
            return err(409, 'That message id was already used for different text.', 'turn_conflict');
          }
          return new Response(JSON.stringify({ chat_id: chat.id, turn_id: replay.turnId, after_seq: replay.afterSeq, title: chat.title }), {
            status: 202,
            headers: { 'Content-Type': 'application/json' },
          });
        }

        const view = viewOf(chat);
        if (view.running && view.activeTurn) {
          return err(409, 'An answer is already in progress.', 'turn_in_progress', {
            active_turn: { turn_id: view.activeTurn.turnId, after_seq: view.activeTurn.afterSeq },
          });
        }
        if (activeTurnsOf(store) >= 3) {
          return err(429, 'Too many answers are running at once. Please wait for one to finish.', 'too_many_active');
        }
        if (retryOf) {
          const latest = chat.turns[chat.turns.length - 1];
          const latestMsg = transcript(chat).find((m) => m.role === 'error' && m.turn_id === retryOf);
          if (!latest || latest.turnId !== retryOf || !latestMsg || latestMsg.retryable !== true) {
            return err(409, 'That answer can no longer be retried.', 'retry_invalid');
          }
        }

        const lastEnd = chat.turns.length ? playTurn(chat.turns[chat.turns.length - 1]).endSeq : 0;
        const turnId = crypto.randomUUID();
        const turn: MockTurn = {
          turnId,
          clientTurnId,
          scenarioId: retryOf ? 'summary' : scenarioFor(text).id,
          startedAt: clockFn(),
          afterSeq: lastEnd,
          text: retryOf ? '' : text,
          retryOf,
          sentAt: now(),
        };
        chat.turns.push(turn);
        chat.updatedAt = now();
        store.chats[chat.id] = chat;
        writeStore(store);
        return new Response(JSON.stringify({ chat_id: chat.id, turn_id: turnId, after_seq: turn.afterSeq, title: chat.title }), {
          status: 202,
          headers: { 'Content-Type': 'application/json' },
        });
      },
    },
    {
      method: 'GET',
      pattern: new RegExp(`${chatPath}/events$`),
      handler: async (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        const mode = fault();
        if (mode === 'poll-404') return noChat();
        if (mode === 'poll-hang') {
          // Never answers; the dispatcher rejects with AbortError when the caller gives up.
          return new Promise<Response>(() => undefined);
        }
        if (mode === 'poll-error') {
          if (pollErrorSince === null) pollErrorSince = Date.now();
          if (Date.now() - pollErrorSince < 15_000) {
            return mockResponse.error(503, '[devMock] injected poll failure');
          }
        }
        const after = Math.max(0, Number(ctx.url.searchParams.get('after')) || 0);
        const wait = Math.min(3, Math.max(0, Number(ctx.url.searchParams.get('wait')) || 0));
        const chat = findChat(readStore(), ctx.params.caseId, ctx.params.chatId);
        if (!chat) return noChat();

        const deadline = Date.now() + wait * 1000;
        for (;;) {
          const fresh = findChat(readStore(), ctx.params.caseId, ctx.params.chatId);
          if (!fresh) return noChat();
          const view = viewOf(fresh);
          const events = view.events.filter((e) => e.seq > after);
          if (events.length > 0 || !view.running || Date.now() >= deadline || ctx.signal?.aborted) {
            const out = coalesce(events);
            return mockResponse.json({
              events: out,
              last_seq: out.length ? out[out.length - 1].seq : after,
              running: view.running,
            });
          }
          await sleep(100, ctx.signal);
        }
      },
    },
    {
      method: 'POST',
      pattern: new RegExp(`${chatPath}/stop$`),
      handler: (ctx) => {
        const g = gate(ctx.params.caseId);
        if (g) return g;
        const store = readStore();
        const chat = findChat(store, ctx.params.caseId, ctx.params.chatId);
        if (!chat) return noChat();
        const view = viewOf(chat);
        if (view.activeTurn) {
          const turn = chat.turns.find((t) => t.turnId === view.activeTurn?.turnId);
          if (turn) turn.stopAt = elapsedSince(turn.startedAt);
          writeStore(store);
        }
        return new Response(null, { status: 204 });
      },
    },
  ];
}

// ---------------------------------------------------------------- console handle

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__devMockChat = {
    reset() {
      writeStore({ chats: {} });
      resetFaultState();
    },
    /** Run scripted turns n times faster (or slower) than real time. */
    speed(n: number) {
      speedFactor = Number.isFinite(n) && n > 0 ? n : 1;
    },
    scenarios: SCENARIOS.map((s) => s.id),
    setClock(fn: () => number) {
      clockFn = fn;
    },
  };
}

// Test hooks (not part of the console handle).
export const __chatMockTesting = {
  setClock: (fn: () => number) => {
    clockFn = fn;
  },
  reset: () => {
    writeStore({ chats: {} });
    resetFaultState();
    speedFactor = 1;
    clockFn = () => Date.now();
  },
};
